// KEEP IN SYNC with the inline THEMES table in frontend/index.html
import { mixHex, lightenHex } from './mix'

export type ThemeMode = 'light' | 'dark'

export interface ThemePalette {
  paper: string
  surface: string
  ink: string
  accent: string
  border: string
}

export interface ThemeDefinition {
  id: string              // stable identifier persisted to DB
  name: string            // user-visible label, shown in tooltip
  light: ThemePalette
  dark: ThemePalette
}

export const THEMES: ThemeDefinition[] = [
  {
    id: 'focused-violet',
    name: 'Focused Violet',
    light: { paper: '#FFFFFF', surface: '#F7F6FB', ink: '#1A1A2E', accent: '#7C3AED', border: '#E8E6F0' },
    dark:  { paper: '#0A0A0A', surface: '#1A1A1A', ink: '#EDEAF7', accent: '#A78BFA', border: '#2E2E2E' },
  },
  {
    id: 'citrus-lift',
    name: 'Citrus Lift',
    light: { paper: '#FFFBF5', surface: '#FFFFFF', ink: '#1F1A17', accent: '#F97316', border: '#F0E6D8' },
    dark:  { paper: '#0F0A06', surface: '#1A1510', ink: '#F5EBDF', accent: '#FB923C', border: '#2E2318' },
  },
  {
    id: 'ocean-mint',
    name: 'Ocean Mint',
    light: { paper: '#F8FAFC', surface: '#FFFFFF', ink: '#0F172A', accent: '#0D9488', border: '#E2E8F0' },
    dark:  { paper: '#0A0A0A', surface: '#141414', ink: '#E2E8F0', accent: '#2DD4BF', border: '#262626' },
  },
  {
    id: 'indigo-pop',
    name: 'Indigo Pop',
    light: { paper: '#FAFAFF', surface: '#FFFFFF', ink: '#1E1B3A', accent: '#EC4899', border: '#E5E4F5' },
    dark:  { paper: '#0A0A0A', surface: '#131313', ink: '#EAE6FF', accent: '#F472B6', border: '#242424' },
  },
]

export const DEFAULT_THEME_ID = 'focused-violet'

export function getTheme(id: string): ThemeDefinition {
  return THEMES.find((t) => t.id === id) ?? THEMES[0]
}

/** Convert a theme palette into the full CSS var map. Computes derived tokens. */
export function paletteToVars(p: ThemePalette): Record<string, string> {
  return {
    '--color-paper': p.paper,
    '--color-paper-dark': mixHex(p.paper, p.ink, 0.04),  // 4% toward ink
    '--color-surface': p.surface,
    '--color-ink': p.ink,
    '--color-ink-muted': mixHex(p.ink, p.paper, 0.45),   // 45% toward paper
    '--color-ink-faint': mixHex(p.ink, p.paper, 0.70),   // 70% toward paper
    '--color-border': p.border,
    '--color-accent': p.accent,
    '--color-accent-light': lightenHex(p.accent, 0.15),
  }
}

/** Apply theme to the DOM. Writes CSS vars to <html> and sets data-theme. */
export function applyThemeToDOM(themeId: string, mode: ThemeMode): void {
  const theme = getTheme(themeId)
  const palette = mode === 'dark' ? theme.dark : theme.light
  const vars = paletteToVars(palette)
  const root = document.documentElement
  for (const [k, v] of Object.entries(vars)) {
    root.style.setProperty(k, v)
  }
  root.setAttribute('data-theme', mode)
  root.setAttribute('data-theme-id', themeId)
}
